/* eslint-disable react/jsx-key */
/* eslint-disable no-unused-vars */
import React, { useState, useEffect } from "react";
import { makeStyles } from "@material-ui/core/styles";
import { Modal, ModalBody } from "reactstrap";

// @material-ui/icons
import Assessment from "@material-ui/icons/Assessment";
import CheckCircle from "@material-ui/icons/CheckCircle";
import Cancel from "@material-ui/icons/Cancel";
import HourglassEmpty from "@material-ui/icons/HourglassEmpty";
import CameraAlt from "@material-ui/icons/CameraAlt";
import Warning from "@material-ui/icons/Warning";
import FilterList from "@material-ui/icons/FilterList";
import Info from "@material-ui/icons/Info";
import Close from "@material-ui/icons/Close";
import CalendarToday from "@material-ui/icons/CalendarToday";
import BarChart from "@material-ui/icons/BarChart";

// Core components
import GridContainer from "components/Grid/GridContainer.js";
import GridItem from "components/Grid/GridItem.js";
import Card from "components/Card/Card.js";
import CardHeader from "components/Card/CardHeader.js";
import CardIcon from "components/Card/CardIcon.js";
import CardBody from "components/Card/CardBody.js";
import CardFooter from "components/Card/CardFooter.js";
import Button from "components/CustomButtons/Button.js";
import TSSelect from "components/Forms/TSSelect.js";

import axios from "utils/httpClient";

// 12 Months mapping for Thai Fiscal Year (Starting October)
const THAI_MONTHS = [
  { no: 1, name: "ตุลาคม", short: "ต.ค.", q: "Q1" },
  { no: 2, name: "พฤศจิกายน", short: "พ.ย.", q: "Q1" },
  { no: 3, name: "ธันวาคม", short: "ธ.ค.", q: "Q1" },
  { no: 4, name: "มกราคม", short: "ม.ค.", q: "Q2" },
  { no: 5, name: "กุมภาพันธ์", short: "ก.พ.", q: "Q2" },
  { no: 6, name: "มีนาคม", short: "มี.ค.", q: "Q2" },
  { no: 7, name: "เมษายน", short: "เม.ย.", q: "Q3" },
  { no: 8, name: "พฤษภาคม", short: "พ.ค.", q: "Q3" },
  { no: 9, name: "มิถุนายน", short: "มิ.ย.", q: "Q3" },
  { no: 10, name: "กรกฎาคม", short: "ก.ค.", q: "Q4" },
  { no: 11, name: "สิงหาคม", short: "ส.ค.", q: "Q4" },
  { no: 12, name: "กันยายน", short: "ก.ย.", q: "Q4" }
];

const useStyles = makeStyles(() => ({
  cardCategory: {
    color: "#777",
    margin: "0",
    fontSize: "13px",
    marginTop: "0",
    paddingTop: "8px",
    marginBottom: "0",
    fontWeight: "600"
  },
  cardTitle: {
    color: "#2c3e50",
    marginTop: "0px",
    minHeight: "auto",
    fontWeight: "800",
    fontFamily: "'Sarabun', 'Roboto', 'Helvetica', 'Arial', sans-serif",
    marginBottom: "3px"
  },
  statNumber: {
    fontSize: "28px",
    fontWeight: "800",
    lineHeight: "1.2"
  },
  kpiCardCompleted: {
    borderRadius: "12px",
    boxShadow: "0 2px 10px rgba(0,0,0,0.06)",
    transition: "all 0.25s ease-in-out",
    border: "1px solid #c8e6c9",
    backgroundColor: "#ffffff",
    "&:hover": {
      boxShadow: "0 8px 24px rgba(46, 125, 50, 0.15)",
      transform: "translateY(-3px)"
    }
  },
  kpiCardIncomplete: {
    borderRadius: "12px",
    boxShadow: "0 2px 12px rgba(255, 152, 0, 0.15)",
    transition: "all 0.25s ease-in-out",
    border: "2px solid #ff9800",
    backgroundColor: "#fffdf9",
    "&:hover": {
      boxShadow: "0 8px 24px rgba(255, 152, 0, 0.25)",
      transform: "translateY(-3px)"
    }
  },
  badgeSuccess: {
    display: "inline-flex",
    alignItems: "center",
    padding: "4px 10px",
    borderRadius: "16px",
    fontSize: "12px",
    fontWeight: "700",
    backgroundColor: "MediumSeaGreen",
    color: "#ffffff"
  },
  badgeOrange: {
    display: "inline-flex",
    alignItems: "center",
    padding: "4px 10px",
    borderRadius: "16px",
    fontSize: "12px",
    fontWeight: "700",
    backgroundColor: "Orange",
    color: "#ffffff"
  },
  yearTileContainer: {
    display: "flex",
    gap: "6px",
    marginTop: "8px",
    overflowX: "auto",
    paddingBottom: "4px"
  },
  yearTile: {
    flex: 1,
    minWidth: "48px",
    borderRadius: "8px",
    padding: "8px 4px",
    textAlign: "center",
    color: "#ffffff",
    cursor: "pointer",
    transition: "all 0.2s cubic-bezier(0.34, 1.56, 0.64, 1)",
    boxShadow: "0 2px 6px rgba(0,0,0,0.12)",
    border: "1px solid rgba(255,255,255,0.2)",
    "&:hover": {
      transform: "translateY(-4px) scale(1.06)",
      boxShadow: "0 8px 18px rgba(0,0,0,0.25)"
    }
  },
  filterPill: {
    padding: "6px 14px",
    borderRadius: "20px",
    cursor: "pointer",
    fontSize: "13px",
    fontWeight: "600",
    border: "1px solid #ddd",
    backgroundColor: "#fff",
    marginRight: "8px",
    marginBottom: "6px",
    display: "inline-block",
    transition: "all 0.2s"
  },
  filterPillActive: {
    backgroundColor: "#00acc1",
    color: "#fff",
    borderColor: "#00acc1",
    boxShadow: "0 2px 8px rgba(0, 172, 193, 0.35)"
  },
  legendBox: {
    backgroundColor: "#ffffff",
    borderRadius: "10px",
    padding: "12px 16px",
    border: "1px solid #e0e0e0",
    marginBottom: "16px",
    fontSize: "13px"
  }
}));

export default function KPIDashboard() {
  const classes = useStyles();

  // Dropdown states
  const [l1Option, setL1Option] = useState([]);
  const [l2Option, setL2Option] = useState([]);
  const [l3Option, setL3Option] = useState([]);
  const [l4Option, setL4Option] = useState([]);

  const [option1, setOption1] = useState("");
  const [option2, setOption2] = useState("");
  const [option3, setOption3] = useState("");
  const [option4, setOption4] = useState("");

  const [oldOption1, setOldOption1] = useState("");
  const [oldOption2, setOldOption2] = useState("");

  const [disable1, setDisable1] = useState(true);
  const [disable2, setDisable2] = useState(true);

  // Data states
  const [rawKpiList, setRawKpiList] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [statusFilter, setStatusFilter] = useState("ALL");
  const [isExporting, setIsExporting] = useState(false);

  // Monthly Dashboard Modal States
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedKpi, setSelectedKpi] = useState(null);
  const [selectedYear, setSelectedYear] = useState(null);
  const [monthData, setMonthData] = useState([]);
  const [isMonthLoading, setIsMonthLoading] = useState(false);
  const [isModalExporting, setIsModalExporting] = useState(false);

  // 1. Fetch Group 1 Options
  useEffect(() => {
    axios("/api/v1/group1")
      .then(result => {
        if (result.data && result.data.data) {
          setL1Option(result.data.data);
        }
      })
      .catch(err => console.error(err));
  }, []);

  // 2. Fetch Group 2 Options
  useEffect(() => {
    if (option1 !== "") {
      axios("/api/v1/group2/" + option1)
        .then(result => {
          if (result.data && result.data.data) {
            setL2Option(result.data.data);
            setDisable1(false);
            setOldOption1(option1);
            if (option1 !== oldOption1) {
              setOption2("");
              setOption3("");
              setDisable2(true);
              setL3Option([]);
            }
          }
        })
        .catch(err => console.error(err));
    }
  }, [option1]);

  // 3. Fetch Group 3 Options
  useEffect(() => {
    if (option2 !== "") {
      axios("/api/v1/subGroup3/" + option2)
        .then(result => {
          if (result.data && result.data.data) {
            setL3Option(result.data.data);
            setDisable2(false);
            setOldOption2(option2);
            if (option2 !== oldOption2) {
              setOption3("");
            }
          }
        })
        .catch(err => console.error(err));
    }
  }, [option2]);

  // 4. Fetch Group 4 Options
  useEffect(() => {
    if (option3 !== "") {
      axios("/api/v1/subGroup4/" + option3)
        .then(result => {
          if (result.data && result.data.data) {
            setL4Option(result.data.data);
          }
        })
        .catch(err => console.error(err));
    }
  }, [option3]);

  // 5. Fetch KPI Data for Dashboard
  useEffect(() => {
    let url = "";
    if (option1 === 4 && option4 !== "") {
      url = `/api/v1/table3/${option4}/pagination?PageNumber=1&PageSize=200`;
    } else if (option1 === 1 && option3 !== "") {
      url = `/api/v1/table2/${option3}/pagination?PageNumber=1&PageSize=200`;
    } else if (option2 !== "") {
      if (option1 !== 1 && option1 !== 4) {
        url = `/api/v1/table/${option2}/pagination?PageNumber=1&PageSize=200`;
      } else {
        url = `/api/v1/tableOption2/${option2}/pagination?PageNumber=1&PageSize=200`;
      }
    } else if (option1 !== "") {
      url = `/api/v1/tableOption/${option1}/pagination?PageNumber=1&PageSize=200`;
    }

    if (url) {
      setIsLoading(true);
      axios(url)
        .then(res => res.data)
        .then(result => {
          if (result && result.data) {
            setRawKpiList(result.data);
          } else {
            setRawKpiList([]);
          }
          setIsLoading(false);
        })
        .catch(err => {
          console.error(err);
          setIsLoading(false);
        });
    }
  }, [option1, option2, option3, option4]);

  // Open Monthly Dashboard Popup
  const handleOpenMonthDashboard = (kpi, year) => {
    setSelectedKpi(kpi);
    setSelectedYear(year);
    setIsModalOpen(true);
    setIsMonthLoading(true);

    axios(`/api/v1/data/month?dataId=${kpi.dataId}&year=${year}&dataGroup=1`)
      .then(res => res.data)
      .then(result => {
        if (result && result.data && Array.isArray(result.data)) {
          setMonthData(result.data);
        } else {
          setMonthData([]);
        }
        setIsMonthLoading(false);
      })
      .catch(err => {
        console.error(err);
        setMonthData([]);
        setIsMonthLoading(false);
      });
  };

  // Helper: Check target compliance
  const checkPassed = (val, targetVal, typeName) => {
    if (val === null || val === undefined || isNaN(targetVal)) return false;
    const t = Number(targetVal);
    const v = Number(val);
    if (!typeName) return v >= t;
    if (typeName.includes("มากกว่าหรือเท่ากับ")) return v >= t;
    if (typeName.includes("มากกว่า")) return v > t;
    if (typeName.includes("น้อยกว่าหรือเท่ากับ")) return v <= t;
    if (typeName.includes("น้อยกว่า")) return v < t;
    if (typeName.includes("เท่ากับ")) return v === t;
    return true;
  };

  const totalKPIs = rawKpiList.length;

  const completedKPIs = rawKpiList.filter(
    k => k.flagTarget === 1 || k.flagTarget === true
  ).length;

  const incompleteKPIs = totalKPIs - completedKPIs;

  const approvedKPIs = rawKpiList.filter(
    k => k.flag === 1 || k.flag === true
  ).length;

  const pendingApprovalKPIs = totalKPIs - approvedKPIs;

  const completedPercent =
    totalKPIs > 0 ? ((completedKPIs / totalKPIs) * 100).toFixed(1) : 0;
  const incompletePercent =
    totalKPIs > 0 ? ((incompleteKPIs / totalKPIs) * 100).toFixed(1) : 0;

  // Filter list by selected status
  const filteredList = rawKpiList.filter(item => {
    const isCompleted = item.flagTarget === 1 || item.flagTarget === true;
    const isApproved = item.flag === 1 || item.flag === true;

    if (statusFilter === "COMPLETED") return isCompleted;
    if (statusFilter === "INCOMPLETE") return !isCompleted;
    if (statusFilter === "APPROVED") return isApproved;
    if (statusFilter === "PENDING_APPROVAL") return !isApproved;
    return true;
  });

  // Export Main Dashboard to PNG
  const handleExportImage = () => {
    setIsExporting(true);
    const target = document.getElementById("kpi-dashboard-capture");
    if (!target) {
      setIsExporting(false);
      return;
    }

    const capture = h2c => {
      h2c(target, {
        scale: 2,
        useCORS: true,
        logging: false,
        backgroundColor: "#f4f6f9"
      })
        .then(canvas => {
          const link = document.createElement("a");
          const dateStr = new Date().toISOString().slice(0, 10);
          link.download = `KPI_Dashboard_${dateStr}.png`;
          link.href = canvas.toDataURL("image/png");
          link.click();
          setIsExporting(false);
        })
        .catch(err => {
          console.error(err);
          setIsExporting(false);
        });
    };

    if (window.html2canvas) {
      capture(window.html2canvas);
    } else {
      const script = document.createElement("script");
      script.src =
        "https://cdnjs.cloudflare.com/ajax/libs/html2canvas/1.4.1/html2canvas.min.js";
      script.onload = () => {
        if (window.html2canvas) capture(window.html2canvas);
      };
      script.onerror = () => {
        alert("ไม่สามารถโหลดไลบรารีสำหรับ Export รูปภาพได้");
        setIsExporting(false);
      };
      document.head.appendChild(script);
    }
  };

  // Export Monthly Modal to PNG
  const handleExportModalImage = () => {
    setIsModalExporting(true);
    const target = document.getElementById("monthly-dashboard-modal-capture");
    if (!target) {
      setIsModalExporting(false);
      return;
    }

    const capture = h2c => {
      h2c(target, {
        scale: 2,
        useCORS: true,
        logging: false,
        backgroundColor: "#f4f6f9"
      })
        .then(canvas => {
          const link = document.createElement("a");
          link.download = `Monthly_KPI_${selectedKpi ? selectedKpi.dataId : ""}_${selectedYear}.png`;
          link.href = canvas.toDataURL("image/png");
          link.click();
          setIsModalExporting(false);
        })
        .catch(err => {
          console.error(err);
          setIsModalExporting(false);
        });
    };

    if (window.html2canvas) {
      capture(window.html2canvas);
    }
  };

  // Build Month Data Map for Modal
  const monthMap = {};
  monthData.forEach(item => {
    if (item && item.month != null) {
      monthMap[item.month] = item;
    }
  });

  const targetVal = selectedKpi && selectedKpi.target ? Number(selectedKpi.target) : 0;

  // Monthly stats for Modal
  const filledMonthsCount = THAI_MONTHS.filter(m => {
    const it = monthMap[m.no];
    return it && it.value !== null && it.value !== undefined;
  }).length;

  const passedMonthsCount = THAI_MONTHS.filter(m => {
    const it = monthMap[m.no];
    return it && it.value !== null && it.value !== undefined && checkPassed(it.value, targetVal, selectedKpi ? selectedKpi.typeName : "");
  }).length;

  const approvedMonthsCount = THAI_MONTHS.filter(m => {
    const it = monthMap[m.no];
    return it && it.isApprove === true;
  }).length;

  const validMonthValues = THAI_MONTHS.map(m => {
    const it = monthMap[m.no];
    return it && it.value !== null && it.value !== undefined ? Number(it.value) : null;
  }).filter(v => v !== null);

  const totalYearValue = validMonthValues.length > 0
    ? validMonthValues.reduce((a, b) => a + b, 0)
    : 0;

  const avgYearValue = validMonthValues.length > 0
    ? totalYearValue / validMonthValues.length
    : 0;

  const maxMonthValue = Math.max(
    ...validMonthValues,
    targetVal > 0 ? targetVal : 1,
    1
  );

  return (
    <div>
      {/* Top Filter Card */}
      <Card style={{ marginTop: "0", marginBottom: "16px" }}>
        <CardHeader
          color="info"
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            flexWrap: "wrap",
            gap: "10px"
          }}
        >
          <div>
            <h4 style={{ color: "#fff", margin: 0, fontWeight: "bold" }}>
              📊 Dashboard ตัวชี้วัด (KPI Monitoring Dashboard)
            </h4>
            <p style={{ color: "rgba(255,255,255,0.85)", margin: "4px 0 0", fontSize: "13px" }}>
              ติดตามสถานะความสมบูรณ์การกรอกข้อมูล (เขียว/ส้ม) และการอนุมัติรายปี
            </p>
          </div>
          <Button
            color="white"
            round
            size="sm"
            onClick={handleExportImage}
            disabled={isExporting}
            style={{ fontWeight: "bold", color: "#00acc1", margin: 0 }}
          >
            <CameraAlt style={{ marginRight: "6px" }} />
            {isExporting ? "กำลังบันทึกรูปภาพ..." : "Export รูปภาพ (.PNG)"}
          </Button>
        </CardHeader>
        <CardBody>
          <GridContainer>
            <GridItem xs={12} sm={6} md={3}>
              <label style={{ fontSize: "12px", color: "#666", fontWeight: "bold" }}>
                1. กลุ่มงานหลัก:
              </label>
              <TSSelect
                zIndex={999}
                options={l1Option}
                onChange={e => setOption1(e)}
              />
            </GridItem>
            <GridItem xs={12} sm={6} md={3}>
              <label style={{ fontSize: "12px", color: "#666", fontWeight: "bold" }}>
                2. หน่วยงาน / งาน:
              </label>
              <TSSelect
                zIndex={998}
                options={l2Option}
                disabled={disable1}
                onChange={e => setOption2(e)}
              />
            </GridItem>
            {(option1 === 1 || option1 === 4) && (
              <GridItem xs={12} sm={6} md={3}>
                <label style={{ fontSize: "12px", color: "#666", fontWeight: "bold" }}>
                  3. งานย่อย:
                </label>
                <TSSelect
                  zIndex={997}
                  options={l3Option}
                  disabled={disable2}
                  onChange={e => setOption3(e)}
                />
              </GridItem>
            )}
            {option1 === 4 && (
              <GridItem xs={12} sm={6} md={3}>
                <label style={{ fontSize: "12px", color: "#666", fontWeight: "bold" }}>
                  4. งานย่อยระดับ 4:
                </label>
                <TSSelect
                  zIndex={996}
                  options={l4Option}
                  onChange={e => setOption4(e)}
                />
              </GridItem>
            )}
          </GridContainer>
        </CardBody>
      </Card>

      {/* Main Printable Area */}
      <div id="kpi-dashboard-capture" style={{ padding: "8px", backgroundColor: "#f4f6f9", borderRadius: "10px" }}>
        
        {/* Color Legend Box */}
        <div className={classes.legendBox}>
          <div style={{ display: "flex", alignItems: "center", marginBottom: "8px" }}>
            <Info style={{ color: "#00acc1", marginRight: "6px", fontSize: "20px" }} />
            <strong style={{ color: "#2c3e50" }}>
              เกณฑ์การจำแนกสีสถานะตัวชี้วัด (Color Status Meaning):
            </strong>
          </div>
          <GridContainer>
            <GridItem xs={12} md={6}>
              <div style={{ display: "flex", alignItems: "flex-start", gap: "8px", marginBottom: "6px" }}>
                <span className={classes.badgeSuccess} style={{ whiteSpace: "nowrap" }}>
                  🟢 สีเขียว
                </span>
                <span style={{ color: "#444" }}>
                  <b>กรอกข้อมูลครบตามรอบ & ผ่านเกณฑ์:</b> รอบ 1 เดือน (กรอกครบทุกเดือน), รอบ 6 เดือน (กรอก &ge; 2 ครั้ง), รอบ 1 ปี (กรอก &ge; 1 ครั้ง)
                </span>
              </div>
            </GridItem>
            <GridItem xs={12} md={6}>
              <div style={{ display: "flex", alignItems: "flex-start", gap: "8px", marginBottom: "6px" }}>
                <span className={classes.badgeOrange} style={{ whiteSpace: "nowrap" }}>
                  🟠 สีส้ม/เหลือง
                </span>
                <span style={{ color: "#444" }}>
                  <b>กรอกยังไม่ครบ หรือ ยังไม่อนุมัติ:</b> กรอกไม่ครบตามรอบ / ผลงานไม่ผ่าน หรือ ในช่องปีที่ <b>ยังไม่ได้กดอนุมัติ (Approve)</b>
                </span>
              </div>
            </GridItem>
          </GridContainer>
        </div>

        {/* Executive Summary Cards */}
        <GridContainer>
          {/* Card 1: Total KPIs */}
          <GridItem xs={12} sm={6} md={3}>
            <Card>
              <CardHeader color="info" stats icon>
                <CardIcon color="info">
                  <Assessment />
                </CardIcon>
                <p className={classes.cardCategory}>ตัวชี้วัดทั้งหมด</p>
                <h3 className={`${classes.cardTitle} ${classes.statNumber}`}>
                  {totalKPIs}{" "}
                  <small style={{ fontSize: "14px", fontWeight: "normal" }}>รายการ</small>
                </h3>
              </CardHeader>
              <CardFooter stats>
                <div style={{ color: "#00acc1", fontSize: "12px", fontWeight: "600" }}>
                  ในหมวดงานที่เลือกปัจจุบัน
                </div>
              </CardFooter>
            </Card>
          </GridItem>

          {/* Card 2: Completed / Passed (Green) */}
          <GridItem xs={12} sm={6} md={3}>
            <Card>
              <CardHeader color="success" stats icon>
                <CardIcon color="success">
                  <CheckCircle />
                </CardIcon>
                <p className={classes.cardCategory}>กรอกครบ / ผ่านเกณฑ์ (เขียว)</p>
                <h3 className={`${classes.cardTitle} ${classes.statNumber}`} style={{ color: "MediumSeaGreen" }}>
                  {completedKPIs}{" "}
                  <small style={{ fontSize: "14px", color: "#666" }}>
                    ({completedPercent}%)
                  </small>
                </h3>
              </CardHeader>
              <CardFooter stats>
                <div style={{ color: "MediumSeaGreen", fontSize: "12px", fontWeight: "600" }}>
                  🟢 ข้อมูลครบถ้วนตามรอบความถี่
                </div>
              </CardFooter>
            </Card>
          </GridItem>

          {/* Card 3: Incomplete / Follow-up (Orange) */}
          <GridItem xs={12} sm={6} md={3}>
            <Card>
              <CardHeader color="warning" stats icon>
                <CardIcon color="warning">
                  <Warning />
                </CardIcon>
                <p className={classes.cardCategory}>กรอกไม่ครบ / ต้องติดตาม (ส้ม)</p>
                <h3 className={`${classes.cardTitle} ${classes.statNumber}`} style={{ color: "Orange" }}>
                  {incompleteKPIs}{" "}
                  <small style={{ fontSize: "14px", color: "#666" }}>
                    ({incompletePercent}%)
                  </small>
                </h3>
              </CardHeader>
              <CardFooter stats>
                <div style={{ color: "Orange", fontSize: "12px", fontWeight: "600" }}>
                  🟠 ต้องกรอกเพิ่ม หรือ ผลงานหลุดเป้า
                </div>
              </CardFooter>
            </Card>
          </GridItem>

          {/* Card 4: Approval Status */}
          <GridItem xs={12} sm={6} md={3}>
            <Card>
              <CardHeader color="primary" stats icon>
                <CardIcon color="primary">
                  <HourglassEmpty />
                </CardIcon>
                <p className={classes.cardCategory}>สถานะการอนุมัติปีล่าสุด</p>
                <h3 className={`${classes.cardTitle} ${classes.statNumber}`} style={{ color: "#3f51b5" }}>
                  {approvedKPIs} / {totalKPIs}
                </h3>
              </CardHeader>
              <CardFooter stats>
                <div style={{ fontSize: "12px", fontWeight: "600" }}>
                  <span style={{ color: "MediumSeaGreen", marginRight: "8px" }}>
                    🟢 อนุมัติแล้ว: {approvedKPIs}
                  </span>
                  <span style={{ color: "Orange" }}>
                    🟠 ยังไม่อนุมัติ: {pendingApprovalKPIs}
                  </span>
                </div>
              </CardFooter>
            </Card>
          </GridItem>
        </GridContainer>

        {/* Completeness Proportion Bar */}
        <Card style={{ padding: "16px", marginBottom: "16px" }}>
          <div style={{ display: "flex", justifyContent: "space-between", marginBottom: "8px" }}>
            <span style={{ fontWeight: "bold", fontSize: "14px", color: "#333" }}>
              สัดส่วนความสมบูรณ์ของการบันทึกข้อมูลตัวชี้วัด (Data Completion Ratio)
            </span>
            <span style={{ fontWeight: "bold", color: "MediumSeaGreen" }}>
              กรอกครบแล้ว {completedPercent}%
            </span>
          </div>
          <div
            style={{
              height: "20px",
              width: "100%",
              backgroundColor: "#e0e0e0",
              borderRadius: "10px",
              display: "flex",
              overflow: "hidden"
            }}
          >
            <div
              style={{
                width: `${completedPercent}%`,
                backgroundColor: "MediumSeaGreen",
                transition: "width 0.5s ease"
              }}
              title={`กรอกครบ/ผ่านเกณฑ์: ${completedPercent}%`}
            />
            <div
              style={{
                width: `${incompletePercent}%`,
                backgroundColor: "Orange",
                transition: "width 0.5s ease"
              }}
              title={`กรอกไม่ครบ/ต้องติดตาม: ${incompletePercent}%`}
            />
          </div>
          <div style={{ display: "flex", justifyContent: "space-between", marginTop: "8px", fontSize: "13px" }}>
            <span style={{ color: "MediumSeaGreen", fontWeight: "bold" }}>
              🟢 กรอกครบถ้วนและผ่านเกณฑ์: {completedKPIs} รายการ ({completedPercent}%)
            </span>
            <span style={{ color: "Orange", fontWeight: "bold" }}>
              🟠 กรอกไม่ครบตามรอบ หรือยังไม่ผ่านเกณฑ์: {incompleteKPIs} รายการ ({incompletePercent}%)
            </span>
          </div>
        </Card>

        {/* Filter Pills */}
        <div style={{ margin: "10px 0 16px", display: "flex", alignItems: "center", flexWrap: "wrap" }}>
          <span style={{ fontSize: "13px", fontWeight: "bold", marginRight: "10px", color: "#555" }}>
            <FilterList style={{ verticalAlign: "middle", fontSize: "18px" }} /> ตัวกรอง:
          </span>
          <span
            className={`${classes.filterPill} ${statusFilter === "ALL" ? classes.filterPillActive : ""}`}
            onClick={() => setStatusFilter("ALL")}
          >
            ทั้งหมด ({totalKPIs})
          </span>
          <span
            className={`${classes.filterPill} ${statusFilter === "COMPLETED" ? classes.filterPillActive : ""}`}
            onClick={() => setStatusFilter("COMPLETED")}
          >
            🟢 กรอกครบ / ผ่านเกณฑ์ ({completedKPIs})
          </span>
          <span
            className={`${classes.filterPill} ${statusFilter === "INCOMPLETE" ? classes.filterPillActive : ""}`}
            onClick={() => setStatusFilter("INCOMPLETE")}
          >
            🟠 กรอกไม่ครบ / ต้องติดตาม ({incompleteKPIs})
          </span>
          <span
            className={`${classes.filterPill} ${statusFilter === "APPROVED" ? classes.filterPillActive : ""}`}
            onClick={() => setStatusFilter("APPROVED")}
          >
            ✅ อนุมัติแล้ว ({approvedKPIs})
          </span>
          <span
            className={`${classes.filterPill} ${statusFilter === "PENDING_APPROVAL" ? classes.filterPillActive : ""}`}
            onClick={() => setStatusFilter("PENDING_APPROVAL")}
          >
            ⏳ ยังไม่อนุมัติ ({pendingApprovalKPIs})
          </span>
        </div>

        {/* Visual KPI Cards Grid */}
        {isLoading ? (
          <div style={{ textAlign: "center", padding: "50px", fontSize: "16px", color: "#888" }}>
            ⏳ กำลังโหลดข้อมูลตัวชี้วัด...
          </div>
        ) : filteredList.length === 0 ? (
          <div style={{ textAlign: "center", padding: "50px 20px", backgroundColor: "#fff", borderRadius: "12px", border: "1px dashed #cfd8dc" }}>
            <p style={{ fontSize: "16px", color: "#607d8b", fontWeight: "600", margin: 0 }}>
              {option1 === "" ? "👆 กรุณาเลือกกลุ่มงานหลักด้านบนเพื่อแสดง Dashboard ตัวชี้วัด" : "ไม่พบข้อมูลตัวชี้วัดในเงื่อนไขที่เลือก"}
            </p>
          </div>
        ) : (
          <GridContainer>
            {filteredList.map((kpi, idx) => {
              const isCompleted = kpi.flagTarget === 1 || kpi.flagTarget === true;
              const isApproved = kpi.flag === 1 || kpi.flag === true;

              // Parse list of years from listData (up to 6 years)
              const sortedYears = (kpi.listData || [])
                .slice(0, 6)
                .reverse()
                .map(d => {
                  const val =
                    kpi.showId === 1 || kpi.showId === 3
                      ? Number(d.value2 || 0)
                      : Number(d.value || 0);
                  return {
                    year: d.key,
                    val: val
                  };
                });

              const latestItem =
                sortedYears.length > 0
                  ? sortedYears[sortedYears.length - 1]
                  : { year: "ล่าสุด", val: 0 };

              return (
                <GridItem xs={12} sm={6} md={4} key={kpi.dataId || idx}>
                  <Card className={isCompleted ? classes.kpiCardCompleted : classes.kpiCardIncomplete}>
                    <CardBody style={{ padding: "16px" }}>
                      
                      {/* Top Header Badges */}
                      <div
                        style={{
                          display: "flex",
                          justifyContent: "space-between",
                          alignItems: "center",
                          marginBottom: "10px",
                          gap: "6px"
                        }}
                      >
                        {/* Tag */}
                        <span
                          style={{
                            fontSize: "11px",
                            fontWeight: "bold",
                            color: "#00acc1",
                            backgroundColor: "rgba(0, 172, 193, 0.12)",
                            padding: "3px 8px",
                            borderRadius: "6px"
                          }}
                        >
                          {kpi.tags || "KPI"}
                        </span>

                        {/* Status Badge 1: Completeness & Target */}
                        <span className={isCompleted ? classes.badgeSuccess : classes.badgeOrange}>
                          {isCompleted ? "🟢 กรอกครบ/ผ่านเกณฑ์" : "🟠 กรอกไม่ครบ/ต้องติดตาม"}
                        </span>
                      </div>

                      {/* KPI Title */}
                      <h4
                        style={{
                          fontSize: "15px",
                          fontWeight: "700",
                          color: isCompleted ? "#2c3e50" : "#d35400",
                          margin: "0 0 10px",
                          lineHeight: "1.4",
                          minHeight: "42px"
                        }}
                      >
                        {kpi.name}
                      </h4>

                      {/* Target & Latest Result Box */}
                      <div
                        style={{
                          display: "grid",
                          gridTemplateColumns: "1.2fr 1fr",
                          gap: "8px",
                          backgroundColor: isCompleted ? "#f9fbfd" : "#fff8e1",
                          padding: "10px",
                          borderRadius: "8px",
                          border: isCompleted ? "1px solid #edf2f7" : "1px solid #ffe082",
                          marginBottom: "10px"
                        }}
                      >
                        <div>
                          <span style={{ fontSize: "11px", color: "#777", display: "block" }}>
                            ค่าเป้าหมาย:
                          </span>
                          <span style={{ fontSize: "13px", fontWeight: "700", color: "#333" }}>
                            {kpi.typeName || ""} {kpi.target ? Number(kpi.target).toLocaleString() : "-"}
                          </span>
                        </div>
                        <div>
                          <span style={{ fontSize: "11px", color: "#777", display: "block" }}>
                            ผลงาน ({latestItem.year}):
                          </span>
                          <span
                            style={{
                              fontSize: "14px",
                              fontWeight: "800",
                              color: isCompleted ? "MediumSeaGreen" : "Orange"
                            }}
                          >
                            {latestItem.val.toLocaleString()} {kpi.unitName || ""}
                          </span>
                        </div>
                      </div>

                      {/* Interactive Year Tiles Header */}
                      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "4px" }}>
                        <span style={{ fontSize: "11px", color: "#333", fontWeight: "700" }}>
                          📅 สถิติรายปี <span style={{ color: "#00acc1", fontWeight: "normal" }}>(คลิกเพื่อดูรายเดือน 👆)</span>:
                        </span>
                        <span
                          style={{
                            fontSize: "11px",
                            fontWeight: "bold",
                            color: isApproved ? "MediumSeaGreen" : "Orange"
                          }}
                        >
                          {isApproved ? "✅ อนุมัติแล้ว" : "⏳ ยังไม่อนุมัติ"}
                        </span>
                      </div>

                      {/* Yearly Color Tiles (Clickable to open Monthly Popup) */}
                      <div className={classes.yearTileContainer}>
                        {sortedYears.map((yd, yIdx) => {
                          const isLatestYear = yIdx === sortedYears.length - 1;
                          const tileColor = !isLatestYear
                            ? "MediumSeaGreen"
                            : isApproved
                            ? "MediumSeaGreen"
                            : "Orange";

                          return (
                            <div
                              key={yIdx}
                              className={classes.yearTile}
                              style={{ backgroundColor: tileColor }}
                              title={`คลิกเพื่อดู Dashboard รายเดือนปี ${yd.year}`}
                              onClick={() => handleOpenMonthDashboard(kpi, yd.year)}
                            >
                              <div style={{ fontSize: "10px", opacity: 0.9 }}>
                                {yd.year}
                              </div>
                              <div style={{ fontSize: "13px", fontWeight: "bold", marginTop: "2px" }}>
                                {yd.val.toLocaleString()}
                              </div>
                            </div>
                          );
                        })}
                      </div>

                    </CardBody>
                  </Card>
                </GridItem>
              );
            })}
          </GridContainer>
        )}
      </div>

      {/* ========================================================== */}
      {/* 🌟 MONTHLY DASHBOARD POPUP MODAL (ขอสวยๆ ตามสั่ง!)        */}
      {/* ========================================================== */}
      <Modal
        isOpen={isModalOpen}
        toggle={() => setIsModalOpen(false)}
        size="xl"
        centered
        style={{ maxWidth: "1150px" }}
      >
        <div
          style={{
            background: "linear-gradient(135deg, #00acc1 0%, #00838f 100%)",
            color: "#ffffff",
            padding: "16px 22px",
            borderRadius: "4px 4px 0 0",
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center"
          }}
        >
          <div>
            <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
              <CalendarToday style={{ fontSize: "22px" }} />
              <h4 style={{ margin: 0, fontWeight: "800", color: "#ffffff", fontSize: "18px" }}>
                Dashboard รายเดือน ปีงบประมาณ {selectedYear}
              </h4>
              {selectedKpi && (
                <span
                  style={{
                    backgroundColor: "rgba(255,255,255,0.25)",
                    padding: "2px 8px",
                    borderRadius: "12px",
                    fontSize: "12px",
                    fontWeight: "bold"
                  }}
                >
                  {selectedKpi.tags || "KPI"}
                </span>
              )}
            </div>
            <p style={{ margin: "4px 0 0", fontSize: "13px", opacity: 0.95 }}>
              {selectedKpi ? selectedKpi.name : ""}
            </p>
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
            <Button
              color="white"
              size="sm"
              round
              onClick={handleExportModalImage}
              disabled={isModalExporting}
              style={{ fontWeight: "bold", color: "#00838f", margin: 0 }}
            >
              <CameraAlt style={{ marginRight: "4px" }} />
              {isModalExporting ? "กำลังบันทึก..." : "Export รูปภาพ (.PNG)"}
            </Button>
            <button
              type="button"
              onClick={() => setIsModalOpen(false)}
              style={{
                background: "rgba(255,255,255,0.2)",
                border: "none",
                borderRadius: "50%",
                width: "36px",
                height: "36px",
                color: "#ffffff",
                cursor: "pointer",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                transition: "background 0.2s"
              }}
            >
              <Close style={{ fontSize: "20px" }} />
            </button>
          </div>
        </div>

        <ModalBody style={{ backgroundColor: "#f4f6f9", padding: "18px" }}>
          <div id="monthly-dashboard-modal-capture" style={{ padding: "4px" }}>
            
            {/* Modal KPI Header Info Chips */}
            {selectedKpi && (
              <div
                style={{
                  backgroundColor: "#ffffff",
                  borderRadius: "10px",
                  padding: "12px 18px",
                  border: "1px solid #e2e8f0",
                  marginBottom: "16px",
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  flexWrap: "wrap",
                  gap: "10px"
                }}
              >
                <div style={{ display: "flex", gap: "20px", flexWrap: "wrap", fontSize: "13px" }}>
                  <div>
                    <span style={{ color: "#718096" }}>ค่าเป้าหมาย: </span>
                    <strong style={{ color: "#2d3748" }}>
                      {selectedKpi.typeName || ""} {selectedKpi.target ? Number(selectedKpi.target).toLocaleString() : "-"}
                    </strong>
                  </div>
                  <div>
                    <span style={{ color: "#718096" }}>ปีงบประมาณ: </span>
                    <strong style={{ color: "#00acc1" }}>{selectedYear}</strong>
                  </div>
                  <div>
                    <span style={{ color: "#718096" }}>บันทึกข้อมูลแล้ว: </span>
                    <strong style={{ color: filledMonthsCount === 12 ? "MediumSeaGreen" : "Orange" }}>
                      {filledMonthsCount} / 12 เดือน
                    </strong>
                  </div>
                </div>

                <div>
                  <span
                    style={{
                      padding: "4px 12px",
                      borderRadius: "16px",
                      fontSize: "12px",
                      fontWeight: "bold",
                      backgroundColor: selectedKpi.flag ? "#e8f5e9" : "#fff3e0",
                      color: selectedKpi.flag ? "MediumSeaGreen" : "Orange",
                      border: `1px solid ${selectedKpi.flag ? "#a5d6a7" : "#ffcc80"}`
                    }}
                  >
                    {selectedKpi.flag ? "🟢 ผ่านการอนุมัติรายปีแล้ว" : "🟠 รอการอนุมัติในรอบปี"}
                  </span>
                </div>
              </div>
            )}

            {isMonthLoading ? (
              <div style={{ textAlign: "center", padding: "60px", color: "#888", fontSize: "16px" }}>
                ⏳ กำลังโหลดข้อมูลสถิติรายเดือน...
              </div>
            ) : (
              <>
                {/* 4 Mini Executive Metric Cards inside Modal */}
                <GridContainer>
                  {/* Card 1: Yearly Metric */}
                  <GridItem xs={12} sm={6} md={3}>
                    <div
                      style={{
                        backgroundColor: "#ffffff",
                        borderRadius: "10px",
                        padding: "14px",
                        border: "1px solid #e2e8f0",
                        boxShadow: "0 2px 6px rgba(0,0,0,0.04)"
                      }}
                    >
                      <span style={{ fontSize: "12px", color: "#718096", fontWeight: "bold" }}>
                        ผลงานทั้งปี ({selectedKpi && (selectedKpi.showId === 1 || selectedKpi.showId === 3) ? "เฉลี่ย" : "รวม"})
                      </span>
                      <div style={{ fontSize: "24px", fontWeight: "800", color: "#2d3748", marginTop: "4px" }}>
                        {(selectedKpi && (selectedKpi.showId === 1 || selectedKpi.showId === 3)
                          ? avgYearValue.toFixed(2)
                          : totalYearValue.toLocaleString())}{" "}
                        <small style={{ fontSize: "12px", color: "#718096", fontWeight: "normal" }}>
                          {selectedKpi ? selectedKpi.unitName : ""}
                        </small>
                      </div>
                    </div>
                  </GridItem>

                  {/* Card 2: Passed Target Months */}
                  <GridItem xs={12} sm={6} md={3}>
                    <div
                      style={{
                        backgroundColor: "#ffffff",
                        borderRadius: "10px",
                        padding: "14px",
                        border: "1px solid #c8e6c9",
                        boxShadow: "0 2px 6px rgba(46,125,50,0.05)"
                      }}
                    >
                      <span style={{ fontSize: "12px", color: "MediumSeaGreen", fontWeight: "bold" }}>
                        🟢 เดือนที่บรรลุเป้าหมาย
                      </span>
                      <div style={{ fontSize: "24px", fontWeight: "800", color: "MediumSeaGreen", marginTop: "4px" }}>
                        {passedMonthsCount}{" "}
                        <small style={{ fontSize: "13px", color: "#666", fontWeight: "normal" }}>
                          / 12 เดือน ({((passedMonthsCount / 12) * 100).toFixed(0)}%)
                        </small>
                      </div>
                    </div>
                  </GridItem>

                  {/* Card 3: Data Completeness */}
                  <GridItem xs={12} sm={6} md={3}>
                    <div
                      style={{
                        backgroundColor: "#ffffff",
                        borderRadius: "10px",
                        padding: "14px",
                        border: `1px solid ${filledMonthsCount === 12 ? "#c8e6c9" : "#ffcc80"}`,
                        boxShadow: "0 2px 6px rgba(0,0,0,0.04)"
                      }}
                    >
                      <span
                        style={{
                          fontSize: "12px",
                          color: filledMonthsCount === 12 ? "MediumSeaGreen" : "Orange",
                          fontWeight: "bold"
                        }}
                      >
                        {filledMonthsCount === 12 ? "🟢 กรอกข้อมูลครบถ้วน" : "🟠 กรอกข้อมูลแล้ว"}
                      </span>
                      <div
                        style={{
                          fontSize: "24px",
                          fontWeight: "800",
                          color: filledMonthsCount === 12 ? "MediumSeaGreen" : "Orange",
                          marginTop: "4px"
                        }}
                      >
                        {filledMonthsCount}{" "}
                        <small style={{ fontSize: "13px", color: "#666", fontWeight: "normal" }}>
                          / 12 เดือน ({((filledMonthsCount / 12) * 100).toFixed(0)}%)
                        </small>
                      </div>
                    </div>
                  </GridItem>

                  {/* Card 4: Approved Months */}
                  <GridItem xs={12} sm={6} md={3}>
                    <div
                      style={{
                        backgroundColor: "#ffffff",
                        borderRadius: "10px",
                        padding: "14px",
                        border: "1px solid #bbdefb",
                        boxShadow: "0 2px 6px rgba(0,0,0,0.04)"
                      }}
                    >
                      <span style={{ fontSize: "12px", color: "#1976d2", fontWeight: "bold" }}>
                        🛡️ เดือนที่อนุมัติแล้ว
                      </span>
                      <div style={{ fontSize: "24px", fontWeight: "800", color: "#1976d2", marginTop: "4px" }}>
                        {approvedMonthsCount}{" "}
                        <small style={{ fontSize: "13px", color: "#666", fontWeight: "normal" }}>
                          / 12 เดือน
                        </small>
                      </div>
                    </div>
                  </GridItem>
                </GridContainer>

                {/* Monthly Visual Bar Chart (12 Months Trend) */}
                <div
                  style={{
                    backgroundColor: "#ffffff",
                    borderRadius: "12px",
                    padding: "18px 22px",
                    border: "1px solid #e2e8f0",
                    margin: "16px 0"
                  }}
                >
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      marginBottom: "14px",
                      flexWrap: "wrap",
                      gap: "8px"
                    }}
                  >
                    <div style={{ display: "flex", alignItems: "center", gap: "6px" }}>
                      <BarChart style={{ color: "#00acc1" }} />
                      <h5 style={{ margin: 0, fontWeight: "700", color: "#2d3748", fontSize: "15px" }}>
                        แนวโน้มผลงานรายเดือน (Monthly Trend 12 เดือน)
                      </h5>
                    </div>
                    <div style={{ display: "flex", gap: "12px", fontSize: "12px", fontWeight: "600" }}>
                      <span style={{ display: "flex", alignItems: "center", gap: "4px" }}>
                        <span
                          style={{
                            width: "10px",
                            height: "10px",
                            borderRadius: "2px",
                            backgroundColor: "MediumSeaGreen",
                            display: "inline-block"
                          }}
                        />
                        🟢 อนุมัติแล้ว
                      </span>
                      <span style={{ display: "flex", alignItems: "center", gap: "4px" }}>
                        <span
                          style={{
                            width: "10px",
                            height: "10px",
                            borderRadius: "2px",
                            backgroundColor: "Orange",
                            display: "inline-block"
                          }}
                        />
                        🟠 ยังไม่อนุมัติ
                      </span>
                      <span style={{ display: "flex", alignItems: "center", gap: "4px" }}>
                        <span
                          style={{
                            width: "10px",
                            height: "10px",
                            borderRadius: "2px",
                            backgroundColor: "#e2e8f0",
                            display: "inline-block"
                          }}
                        />
                        ⚪ ยังไม่บันทึก
                      </span>
                    </div>
                  </div>

                  {/* Bars Container */}
                  <div
                    style={{
                      display: "flex",
                      alignItems: "flex-end",
                      height: "150px",
                      gap: "10px",
                      paddingTop: "24px",
                      borderBottom: "2px solid #edf2f7"
                    }}
                  >
                    {THAI_MONTHS.map(m => {
                      const item = monthMap[m.no];
                      const hasData = item && item.value !== null && item.value !== undefined;
                      const val = hasData ? Number(item.value) : null;
                      const isApprove = item ? item.isApprove === true : false;

                      const barHeight =
                        hasData && maxMonthValue > 0
                          ? Math.max(Math.min((Math.abs(val) / maxMonthValue) * 105, 105), 10)
                          : 6;

                      const barColor = !hasData
                        ? "#e2e8f0"
                        : isApprove
                        ? "MediumSeaGreen"
                        : "Orange";

                      return (
                        <div
                          key={m.no}
                          style={{
                            flex: 1,
                            display: "flex",
                            flexDirection: "column",
                            alignItems: "center",
                            height: "100%",
                            justifyContent: "flex-end"
                          }}
                        >
                          <span
                            style={{
                              fontSize: "11px",
                              fontWeight: "700",
                              color: hasData ? "#2d3748" : "#a0aec0",
                              marginBottom: "4px"
                            }}
                          >
                            {hasData ? (Number.isInteger(val) ? val : val.toFixed(1)) : "-"}
                          </span>
                          <div
                            style={{
                              width: "100%",
                              maxWidth: "36px",
                              height: `${barHeight}px`,
                              backgroundColor: barColor,
                              borderRadius: "6px 6px 0 0",
                              transition: "all 0.3s ease",
                              boxShadow: hasData ? "0 2px 6px rgba(0,0,0,0.12)" : "none"
                            }}
                            title={`${m.name}: ${hasData ? val : "ยังไม่มีข้อมูล"} (${
                              isApprove ? "อนุมัติแล้ว" : "ยังไม่อนุมัติ"
                            })`}
                          />
                        </div>
                      );
                    })}
                  </div>

                  {/* X-Axis Labels */}
                  <div style={{ display: "flex", gap: "10px", marginTop: "8px" }}>
                    {THAI_MONTHS.map(m => (
                      <div key={m.no} style={{ flex: 1, textAlign: "center" }}>
                        <div style={{ fontSize: "12px", fontWeight: "700", color: "#4a5568" }}>
                          {m.short}
                        </div>
                        <div style={{ fontSize: "10px", color: "#a0aec0", fontWeight: "600" }}>
                          {m.q}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                {/* 12 Months Detail Cards (4 Quarters) */}
                <GridContainer>
                  {["Q1", "Q2", "Q3", "Q4"].map(qKey => {
                    const qMonths = THAI_MONTHS.filter(m => m.q === qKey);
                    const qTitle =
                      qKey === "Q1"
                        ? "ไตรมาส 1 (ต.ค. - ธ.ค.)"
                        : qKey === "Q2"
                        ? "ไตรมาส 2 (ม.ค. - มี.ค.)"
                        : qKey === "Q3"
                        ? "ไตรมาส 3 (เม.ย. - มิ.ย.)"
                        : "ไตรมาส 4 (ก.ค. - ก.ย.)";

                    return (
                      <GridItem xs={12} sm={6} md={3} key={qKey}>
                        <div
                          style={{
                            backgroundColor: "#f8fafc",
                            borderRadius: "10px",
                            padding: "12px",
                            border: "1px solid #e2e8f0",
                            height: "100%"
                          }}
                        >
                          <div
                            style={{
                              fontSize: "13px",
                              fontWeight: "800",
                              color: "#00838f",
                              marginBottom: "8px",
                              borderBottom: "1px solid #cbd5e0",
                              paddingBottom: "4px"
                            }}
                          >
                            {qTitle}
                          </div>
                          <div style={{ display: "flex", flexDirection: "column", gap: "8px" }}>
                            {qMonths.map(m => {
                              const item = monthMap[m.no];
                              const hasData = item && item.value !== null && item.value !== undefined;
                              const val = hasData ? Number(item.value) : null;
                              const isApprove = item ? item.isApprove === true : false;
                              const isPassed = hasData
                                ? checkPassed(val, targetVal, selectedKpi ? selectedKpi.typeName : "")
                                : false;

                              let cardBorder = "#e2e8f0";
                              let cardBg = "#ffffff";
                              let badgeColor = "#a0aec0";
                              let badgeText = "ยังไม่บันทึก";

                              if (hasData) {
                                if (isApprove) {
                                  cardBorder = "MediumSeaGreen";
                                  cardBg = "#f0fdf4";
                                  badgeColor = "MediumSeaGreen";
                                  badgeText = "🟢 อนุมัติแล้ว";
                                } else {
                                  cardBorder = "Orange";
                                  cardBg = "#fffdf7";
                                  badgeColor = "Orange";
                                  badgeText = "🟠 ยังไม่อนุมัติ";
                                }
                              }

                              return (
                                <div
                                  key={m.no}
                                  style={{
                                    backgroundColor: cardBg,
                                    border: `1.5px solid ${cardBorder}`,
                                    borderRadius: "8px",
                                    padding: "8px 10px",
                                    display: "flex",
                                    justifyContent: "space-between",
                                    alignItems: "center"
                                  }}
                                >
                                  <div>
                                    <div style={{ fontSize: "13px", fontWeight: "700", color: "#2d3748" }}>
                                      {m.name}
                                    </div>
                                    {item && (item.dataValue1 != null || item.dataValue2 != null) && (
                                      <div style={{ fontSize: "10px", color: "#718096" }}>
                                        ตั้ง: {item.dataValue1 ?? "-"} / หาร: {item.dataValue2 ?? "-"}
                                      </div>
                                    )}
                                  </div>
                                  <div style={{ textAlign: "right" }}>
                                    <div
                                      style={{
                                        fontSize: "15px",
                                        fontWeight: "800",
                                        color: hasData
                                          ? isApprove
                                            ? "MediumSeaGreen"
                                            : "Orange"
                                          : "#a0aec0"
                                      }}
                                    >
                                      {hasData ? val.toLocaleString() : "-"}
                                    </div>
                                    <div style={{ fontSize: "10px", fontWeight: "700", color: badgeColor }}>
                                      {badgeText}
                                    </div>
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      </GridItem>
                    );
                  })}
                </GridContainer>
              </>
            )}
          </div>
        </ModalBody>
      </Modal>
    </div>
  );
}
